import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'

import { config } from '../../config'
import { theme } from '../../themes/theme'
import { FlexColumn, MedBGContainer, Divider } from '../styles/Containers'
import { InfoMessage } from '../common/UserMessage'
import { FormControlLabel, Checkbox } from '../styles/Checkbox'
import { formSection, formWording } from '../styles/FormStyleConfig'
import { BoldSpan, ErrorText } from '../styles/Text'
import UploadFile from '../files/UploadFile'
import BrowserFile from '../files/BrowserFile'
import DatabaseFile from '../files/DatabaseFile'

const Link = styled.a`
  color: ${theme.colors.text.link};
`

const ListColumn = styled(FlexColumn)`
  padding-left: 15px;
`

function InformationTypeForm({ format = 'default', perspective = 'requestor', disabled = false }) {
  const { watch, setValue, formState, register, getValues } = useFormContext()
  const [defaultInfoType, setDefaultInfoType] = useState()

  useEffect(() => {
    setDefaultInfoType(getValues('infoType') || [])
  }, [])

  const existingFiles = watch('existingThirdPartyFiles')
  const files = watch('thirdPartyFiles')
  const infoType = watch('infoType')

  function handleUploadFiles(acceptedFiles) {
    const startingFiles = files || []
    setValue('thirdPartyFiles', startingFiles.concat(acceptedFiles))
  }

  function handleDelete(file) {
    const filteredFiles = files.filter(f => f !== file)
    setValue('thirdPartyFiles', filteredFiles)
  }

  function handleDeleteExistingFile(fileId) {
    const filteredFiles = existingFiles.filter(f => f.id !== fileId)
    setValue('existingThirdPartyFiles', filteredFiles)
  }

  const infoTypeError = !!formState.errors.infoType
  const infoTypeHelperText = formState.errors?.infoType?.message
  const fileError = !!formState.errors.thirdPartyFiles
  const fileHelperText = formState.errors?.thirdPartyFiles?.message

  const TitleComponent = formSection[format].font.formTitle
  const TextComponent = formSection[format].font.bodyText

  const capitalizedPossessive = formWording[perspective].possessive.charAt(0).toUpperCase() + formWording[perspective].possessive.slice(1)
  const typeOptions = [
    { value: 'GENERAL', label: 'General information' },
    {
      value: 'PERSONAL',
      label:
        `${capitalizedPossessive} own personal information (Please provide all` +
        ` ${formWording[perspective].possessive} alternate names in the request details)`
    },
    { value: 'THIRD_PARTY', label: 'Personal information about another individual' }
  ]

  return (
    <FlexColumn gap={formSection[format].gap.div}>
      <TitleComponent>Type of Information</TitleComponent>
      {formSection[format].divider && <Divider />}

      {defaultInfoType !== undefined && (
        <FlexColumn gap="10px">
          <TextComponent>What kind of information do {formWording[perspective].pronoun} want to access?</TextComponent>
          <ListColumn gap="0">
            {typeOptions.map((type, i) => (
              <FormControlLabel
                {...register('infoType')}
                key={i}
                control={<Checkbox defaultChecked={!!defaultInfoType.find(f => f === type.value)} />}
                label={type.label}
                value={type.value}
                format={format}
                disabled={disabled}
              />
            ))}
          </ListColumn>
          {infoTypeError && <ErrorText role="alert">{infoTypeHelperText}</ErrorText>}
        </FlexColumn>
      )}
      {infoType && infoType.includes('THIRD_PARTY') && (
        <FlexColumn gap={formSection[format].gap.subDiv}>
          <InfoMessage>
            For personal information about another individual, you must either <BoldSpan>a)</BoldSpan> upload that person’s{' '}
            {config.foi.request.generalAuthConfig ? (
              <Link href={config.foi.request.generalAuthConfig} target="_blank" rel="noopener noreferrer">
                Signed Consent for Disclosure{' '}
              </Link>
            ) : (
              <>Signed Consent for Disclosure </>
            )}
            or <BoldSpan>b)</BoldSpan> upload Proof of Authority to act on the person’s behalf.
          </InfoMessage>
          {((files && files.length > 0) || (existingFiles && existingFiles.length > 0)) && (
            <FlexColumn gap="10px">
              <TextComponent>The below file(s) will be uploaded as signed consent.</TextComponent>
              <MedBGContainer padding="5px 20px 5px 5px">
                {existingFiles &&
                  existingFiles.length > 0 &&
                  existingFiles.map(f => {
                    return (
                      <DatabaseFile
                        key={f.id}
                        file={f}
                        onDelete={handleDeleteExistingFile}
                        disabled={disabled}
                        buttonAction="softDelete"
                        spacing="medium"
                      />
                    )
                  })}
                {files &&
                  files.length > 0 &&
                  files.map((f, i) => {
                    return <BrowserFile key={i} file={f} onDelete={() => handleDelete(f)} loading={disabled} />
                  })}
              </MedBGContainer>
            </FlexColumn>
          )}
          {!disabled && (
            <FlexColumn gap="10px">
              <TextComponent>Drag and drop or browse below to upload signed consent(s).</TextComponent>
              <UploadFile onUpload={handleUploadFiles} condensed={formSection[format].uploadCondensed} />
              {fileError && <ErrorText role="alert">{fileHelperText}</ErrorText>}
            </FlexColumn>
          )}
        </FlexColumn>
      )}
    </FlexColumn>
  )
}

InformationTypeForm.propTypes = {
  format: PropTypes.oneOf(['default', 'condensed']),
  perspective: PropTypes.oneOf(['requestor', 'staff']),
  disabled: PropTypes.bool
}

export default InformationTypeForm
